import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { environment } from "../../environments/environment";
import { PusherService } from "./pusher.service";
import Pusher from "pusher-js";
import { SecureStorageService } from "./secure-storage.service";
import { Router } from "@angular/router";

export interface Viewer {
  id: number;
  name: string;
  email: string;
  profile_picture: string;
}

export interface StreamEndedPayload {
  slug: string;
  message: string;
}

@Injectable({
  providedIn: "root", // rend le service globalement disponible
})
export class ViewersService {
  private viewersSubject = new BehaviorSubject<Viewer[]>([]);
  private totalViewersSubject = new BehaviorSubject<number>(0);
  private streamNameSubject = new BehaviorSubject<string>("");
  private viewerLeftSubject = new Subject<any>();
  private streamEndedSubject = new Subject<StreamEndedPayload>();
  private token: string = "";
  private httpOptions: { headers: HttpHeaders } = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: "",
    }),
  };

  viewers$ = this.viewersSubject.asObservable();
  totalViewers$ = this.totalViewersSubject.asObservable();
  streamName$ = this.streamNameSubject.asObservable();
  viewerLeft$ = this.viewerLeftSubject.asObservable();
  streamEnded$ = this.streamEndedSubject.asObservable();

  private pusher: Pusher | null = null;
  private channels: Map<string, any> = new Map();

  private chatState = new BehaviorSubject<boolean>(true);
  public chatState$ = this.chatState.asObservable();

  constructor(
    private http: HttpClient,
    private secureStorage: SecureStorageService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.token = this.secureStorage.getItem<string>("token") || "";
    this.httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      }),
    };

    if (!isPlatformBrowser(this.platformId)) return;

    this.pusher = new Pusher(environment.pusher.key, {
      cluster: environment.pusher.cluster,
      wsHost: environment.pusher.wsHost,
      wsPort: environment.pusher.wsPort,
      wssPort: environment.pusher.wssPort,
      forceTLS: true,
      disableStats: true,
      enabledTransports: ["ws", "wss"],
      authEndpoint: environment.pusher.authEndpoint,
      auth: {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      },
    });

    console.log("Pusher initialisé avec la clé:", environment.pusher.key);
  }

  loadViewers(streamSlug: string): void {
    this.http
      .get<any>(`${environment.apiUrl}/streams/${streamSlug}/viewers`, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      })
      .subscribe({
        next: (response) => {
          this.updateViewersData(response.data);
          this.subscribeToViewersUpdates(streamSlug);
        },
        error: (error) => {
          console.error("Erreur lors du chargement des spectateurs:", error);
        },
      });
  }

  private subscribeToViewersUpdates(streamSlug: string): void {
    if (!this.pusher) return;
    const channelName = `private-stream.${streamSlug}`;

    const channel = this.pusher.subscribe(channelName);

    // Nouveau spectateur
    channel.bind("ViewerJoined", (data: any) => {
      console.log("📡 Nouveau spectateur:", data);
      this.updateViewersData(data);
    });

    // Spectateur qui quitte
    channel.bind("ViewerLeft", (data: any) => {
      console.log("👋 Spectateur quitté:", data);
      const currentViewers = this.viewersSubject.getValue();
      const updatedViewers = currentViewers.filter(
        (v) => v.id !== data.viewer_id
      );
      this.viewersSubject.next(updatedViewers);
      this.totalViewersSubject.next(updatedViewers.length);

      // Émettre l'événement pour que les composants puissent réagir
      console.log("📡 Émission de l'événement viewerLeft:", data);
      this.viewerLeftSubject.next(data);
      
    });

    channel.bind("StreamEnded", (data: StreamEndedPayload) => {
      console.log("🏁 Live terminé:", data);
      this.streamEndedSubject.next(data);
    });
  }

  setChatState(isEnabled: boolean) {
    this.chatState.next(isEnabled);
  }

  unsubscribeFromViewersUpdates(streamSlug: string): void {
    if (!this.pusher) return;
    const channelName = `private-stream.${streamSlug}`;
    this.pusher.unsubscribe(channelName);
  }

  private updateViewersData(data: any): void {
    if (data.viewers) {
      this.viewersSubject.next(data.viewers);
    }

    if (data.total_viewers !== undefined) {
      this.totalViewersSubject.next(data.total_viewers);
    }

    if (data.stream) {
      this.streamNameSubject.next(data.stream);
    }
  }

  resetViewersData(): void {
    this.viewersSubject.next([]);
    this.totalViewersSubject.next(0);
    this.streamNameSubject.next("");
  }
}
